import type { NumericTextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Slider } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useCallback, useEffect, useMemo, useState } from "react";

import { formatCurrency } from "~/shared/utils/string-fns.ts";

import {
	SliderFilter,
	SliderFilterActions,
	SliderFilterInput,
	SliderFilterInputs,
} from "../slider-filter/slider-filter.tsx";

type RateFilterProps = {
	max: number | null;
	min: number | null;
	onChange: (value: [number, number] | null) => void;
	step?: number;
};

type SliderChangeEvent = {
	value: number;
};

const DEFAULT_STEP = 1;

const clamp = (value: number, minValue: number, maxValue: number): number => {
	return Math.min(Math.max(value, minValue), maxValue);
};

const snapToStep = (value: number, step: number): number => {
	if (!Number.isFinite(step) || step <= 0) {
		return value;
	}

	return Math.round(value / step) * step;
};

export const RateFilter = ({ max, min, onChange, step }: RateFilterProps) => {
	const [selectedMax, setSelectedMax] = useState<number | null>(null);

	const hasValidRange =
		typeof min === "number" &&
		typeof max === "number" &&
		Number.isFinite(min) &&
		Number.isFinite(max) &&
		min < max;
	const normalizedMin = hasValidRange ? min : 0;
	const normalizedMax = hasValidRange ? max : 0;
	const resolvedStep = step ?? DEFAULT_STEP;
	const currentMax = selectedMax ?? normalizedMax;

	const formatter = useMemo(() => {
		return new Intl.NumberFormat("en-US", {
			currency: "USD",
			maximumFractionDigits: 2,
			style: "currency",
		});
	}, []);

	const selectedRangeText = selectedMax
		? `(Max ${formatter.format(selectedMax)})`
		: "";

	useEffect(() => {
		setSelectedMax(null);
	}, [max, min]);

	const updateSelectedMax = useCallback(
		(value: number) => {
			const nextMax = clamp(value, normalizedMin, normalizedMax);

			setSelectedMax(nextMax);
			onChange([normalizedMin, nextMax]);
		},
		[normalizedMax, normalizedMin, onChange],
	);

	const handleSliderChange = (event: SliderChangeEvent) => {
		updateSelectedMax(snapToStep(event.value, resolvedStep));
	};

	const handleMaxChange = (event: NumericTextBoxChangeEvent) => {
		if (event.value === null) {
			return;
		}

		updateSelectedMax(event.value);
	};

	return (
		<SliderFilter
			hasValidRange={hasValidRange}
			label={"Rate"}
			popupTestId={"price-filter-popup"}
			selectedText={selectedRangeText}
			triggerTestId={"price-filter"}
		>
			{({ closePopup }) => {
				const handleReset = () => {
					setSelectedMax(null);
					onChange(null);
					closePopup();
				};

				return (
					<>
						<Slider
							largeStep={resolvedStep}
							max={normalizedMax}
							min={normalizedMin}
							onChange={handleSliderChange}
							step={resolvedStep}
							style={{ width: "100%" }}
							value={currentMax}
						/>
						<SliderFilterInputs>
							<SliderFilterInput disabled={true}>
								{formatCurrency(normalizedMin)}
							</SliderFilterInput>

							<SliderFilterInput>
								<Label editorId={"rateFilterMax"}>Max</Label>
								<NumericTextBox
									format={"c2"}
									id={"rateFilterMax"}
									max={normalizedMax}
									min={normalizedMin}
									onChange={handleMaxChange}
									spinners={false}
									value={currentMax}
									width={90}
								/>
							</SliderFilterInput>
						</SliderFilterInputs>
						<SliderFilterActions>
							<Button onClick={handleReset}>Reset</Button>
						</SliderFilterActions>
					</>
				);
			}}
		</SliderFilter>
	);
};
